import type { PaymentsConfigResponse } from '@laplace/contracts'

type PaddleConfig = NonNullable<PaymentsConfigResponse['paddle']>

interface PaddleEvent {
  name?: string
  data?: {
    transaction_id?: string
    currency_code?: string
    customer?: { email?: string | null } | null
    totals?: { subtotal?: number; tax?: number; total?: number } | null
  } | null
}

interface PaddleGlobal {
  Environment: { set: (environment: 'sandbox') => void }
  Initialize: (options: {
    token: string
    eventCallback?: (event: PaddleEvent) => void
  }) => void
  Checkout: {
    open: (options: {
      transactionId: string
      customer?: { email: string }
      settings?: {
        displayMode?: 'overlay' | 'inline'
        variant?: 'one-page' | 'multi-page'
        frameTarget?: string
        frameInitialHeight?: number
        frameStyle?: string
        theme?: 'light' | 'dark'
        locale?: string
        allowLogout?: boolean
      }
    }) => void
    close: () => void
  }
}

/** Totals Paddle shows in its checkout (major units, tax for the buyer). */
export interface PaddleCheckoutTotals {
  currencyCode: string
  subtotal: number
  tax: number
  total: number
}

declare global {
  interface Window {
    Paddle?: PaddleGlobal
  }
}

export const PADDLE_JS_URL = 'https://cdn.paddle.com/paddle/v2/paddle.js'

/** The visitor closed the checkout without paying. */
export class PaymentCanceledError extends Error {
  constructor() {
    super('Payment canceled')
    this.name = 'PaymentCanceledError'
  }
}

type Listener = (event: PaddleEvent) => void
const listeners = new Set<Listener>()
let ready: Promise<PaddleGlobal> | null = null

function injectScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.Paddle) {
      resolve()
      return
    }
    const script = document.createElement('script')
    script.src = PADDLE_JS_URL
    script.async = true
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('Paddle.js failed to load'))
    document.head.appendChild(script)
  })
}

/**
 * Loads and initializes Paddle.js once per page. Paddle allows a single
 * Initialize call, so its events go through a module-level bus.
 */
export function loadPaddle(config: PaddleConfig): Promise<PaddleGlobal> {
  ready ??= injectScript()
    .then(() => {
      const paddle = window.Paddle
      if (!paddle) throw new Error('Paddle.js is unavailable')
      if (config.environment === 'sandbox') paddle.Environment.set('sandbox')
      paddle.Initialize({
        token: config.clientToken,
        eventCallback: (event) => {
          for (const listener of listeners) listener(event)
        },
      })
      return paddle
    })
    .catch((error: unknown) => {
      ready = null
      throw error
    })
  return ready
}

/**
 * Opens the Paddle overlay for a transaction created by the API. Resolves
 * when Paddle reports the payment completed (with the email the customer
 * paid with); rejects with PaymentCanceledError when it is closed unpaid.
 * Completion here is the customer's view only: the server confirms it.
 */
export async function openPaddleCheckout(
  config: PaddleConfig,
  transactionId: string,
  options: { email?: string | null; locale?: string } = {},
): Promise<{ email: string | null }> {
  const paddle = await loadPaddle(config)
  return new Promise((resolve, reject) => {
    const listener: Listener = (event) => {
      if (event.name === 'checkout.completed') {
        listeners.delete(listener)
        resolve({ email: event.data?.customer?.email ?? null })
        paddle.Checkout.close()
      } else if (event.name === 'checkout.closed') {
        listeners.delete(listener)
        reject(new PaymentCanceledError())
      }
    }
    listeners.add(listener)
    paddle.Checkout.open({
      transactionId,
      ...(options.email ? { customer: { email: options.email } } : {}),
      settings: {
        displayMode: 'overlay',
        locale: options.locale,
        allowLogout: false,
      },
    })
  })
}

function checkoutTotals(event: PaddleEvent): PaddleCheckoutTotals | null {
  const totals = event.data?.totals
  const currencyCode = event.data?.currency_code
  if (
    !totals ||
    !currencyCode ||
    typeof totals.subtotal !== 'number' ||
    typeof totals.tax !== 'number' ||
    typeof totals.total !== 'number'
  ) {
    return null
  }
  return {
    currencyCode,
    subtotal: totals.subtotal,
    tax: totals.tax,
    total: totals.total,
  }
}

export interface PaddleInlineCheckout {
  /**
   * Resolves when Paddle reports the payment completed (with the payer's
   * email); rejects with PaymentCanceledError once closed unpaid. The server
   * still confirms the payment.
   */
  completed: Promise<{ email: string | null }>
  close: () => void
}

/**
 * Embeds Paddle's checkout for a transaction created by the API into the
 * element with class `frameTarget` (inline checkout: our own dialog keeps the
 * order summary, Paddle's frame takes the payment). `onTotals` follows the
 * totals Paddle shows, tax included, for the summary beside the frame.
 */
export function openPaddleInlineCheckout(
  config: PaddleConfig,
  transactionId: string,
  options: {
    frameTarget: string
    email?: string | null
    locale?: string
    onTotals?: (totals: PaddleCheckoutTotals) => void
  },
): PaddleInlineCheckout {
  let closed = false
  let settle: ((error: Error) => void) | null = null
  let opened: PaddleGlobal | null = null

  const completed = loadPaddle(config).then(
    (paddle) =>
      new Promise<{ email: string | null }>((resolve, reject) => {
        if (closed) {
          reject(new PaymentCanceledError())
          return
        }
        const listener: Listener = (event) => {
          const id = event.data?.transaction_id
          if (id && id !== transactionId) return
          // Totals change with the customer's details too (a new country,
          // a VAT number, a discount): every event that carries them counts.
          const totals = checkoutTotals(event)
          if (totals) options.onTotals?.(totals)
          if (event.name === 'checkout.completed') {
            listeners.delete(listener)
            resolve({ email: event.data?.customer?.email ?? null })
          } else if (event.name === 'checkout.closed') {
            listeners.delete(listener)
            reject(new PaymentCanceledError())
          } else if (event.name === 'checkout.error') {
            listeners.delete(listener)
            reject(new Error('Paddle checkout failed'))
          }
        }
        settle = (error) => {
          listeners.delete(listener)
          reject(error)
        }
        listeners.add(listener)
        opened = paddle
        paddle.Checkout.open({
          transactionId,
          ...(options.email ? { customer: { email: options.email } } : {}),
          settings: {
            displayMode: 'inline',
            variant: 'one-page',
            frameTarget: options.frameTarget,
            frameInitialHeight: 450,
            frameStyle:
              'width: 100%; min-width: 312px; background-color: transparent; border: none;',
            theme: 'light',
            locale: options.locale,
            allowLogout: false,
          },
        })
      }),
  )

  return {
    completed,
    close: () => {
      if (closed) return
      closed = true
      settle?.(new PaymentCanceledError())
      try {
        opened?.Checkout.close()
      } catch {
        // The frame's container may already be gone with the dialog.
      }
    },
  }
}

/** Test seam: forget the loaded instance. */
export function resetPaddleForTests(): void {
  ready = null
  listeners.clear()
}
